import { Box, Skeleton, Tooltip, Typography } from '@mui/material';
import type {
  DataGridProProps,
  GridAlignment,
  GridColDef,
  GridColumnHeaderParams,
} from '@mui/x-data-grid-pro';
import { GRID_TREE_DATA_GROUPING_FIELD } from '@mui/x-data-grid-pro';
import { Fragment, useMemo } from 'react';
import { ImageMediaCell } from '@/features/DataGrid';
import { selectCurrency } from '@/entities/session';
import { DEFAULT_COLUMN_WIDTH } from '@/shared/constants';
import { parseMediaColumnValue } from '@/shared/data';
import { useNumberFormatter } from '@/shared/hooks';
import { formatDate } from '@/shared/lib';
import { useAppSelector } from '@/shared/model';
import { DimensionColumnDataType, toFormatType } from '@/shared/types';
import type {
  ColumnsConfig,
  MetricColumnDataType,
  MetricDataFormatType,
} from '@/shared/types';
import type { MinMaxData } from '@/shared/ui';
import { CohortAgeHeader } from '../CohortAgeHeader/CohortAgeHeader';
import { DATA_COLUMN_MIN_WIDTH } from '../DataTable/constants';
import { getMetricTypeProps } from '../DataTable/useTableProps';

const DEFAULT_GROUPING_COLUMN_WIDTH = 300;

interface Params {
  columnsWidth: Record<string, number | undefined>;
  columnsConfig: ColumnsConfig;
  dimensions: string[];
  metric: string;
  dataColumns: string[];
  minMax?: MinMaxData[string];
}

export const useTreeDataGridProps = ({
  columnsConfig,
  columnsWidth,
  dimensions,
  metric,
  minMax,
  dataColumns,
}: Params): {
  columns: GridColDef[];
  groupingColDef: DataGridProProps['groupingColDef'];
  getTreeDataPath: DataGridProProps['getTreeDataPath'];
} => {
  const userCurrency = useAppSelector(selectCurrency);
  const formattersMap = useNumberFormatter(userCurrency);
  const columns: GridColDef[] = useMemo(
    () => {
      const metricConfig = columnsConfig[metric];
      const metricFormatter =
        formattersMap[metricConfig.type as MetricColumnDataType][
          toFormatType(metricConfig.dataType as MetricDataFormatType)
        ];

      return dataColumns.map((column) => {
        return {
          ...getMetricTypeProps({
            minMax,
            metric,
            formatter: metricFormatter,
          }),
          field: column,
          minWidth: DATA_COLUMN_MIN_WIDTH,
          width: columnsWidth[column] || DEFAULT_COLUMN_WIDTH,
          disableReorder: true,
          align: 'right' as GridAlignment,
          headerClassName: 'metricHeader',
          cellClassName: 'metricCell',
          renderHeader: ({ colDef }: GridColumnHeaderParams) => {
            if (!colDef) {
              return null;
            }

            return <CohortAgeHeader cohortAge={colDef.field} />;
          },
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataColumns, dimensions, metric, minMax, columnsConfig, columnsWidth],
  );

  return {
    columns,
    getTreeDataPath: (row) => row.path,
    groupingColDef: {
      headerClassName: 'groupingHeader',
      cellClassName: 'groupingCell',
      hideDescendantCount: true,
      renderHeader: () => {
        const tooltipTitle = dimensions
          .map((dimension) => {
            return columnsConfig[dimension].name;
          })
          .join(' ⮑ ');

        return (
          <Tooltip title={tooltipTitle}>
            <Box
              component="span"
              sx={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {dimensions.map((dimension, index) => {
                return (
                  <Fragment key={dimension}>
                    {index !== 0 && (
                      <Typography
                        component="span"
                        sx={{ fontSize: '0.66rem', mx: 0.5 }}
                      >
                        ⮑
                      </Typography>
                    )}
                    {columnsConfig[dimension].name}
                  </Fragment>
                );
              })}
            </Box>
          </Tooltip>
        );
      },
      valueGetter: (_, row) => {
        if (row.isTotal) {
          return 'Total';
        }

        const prop = dimensions[row.path.length - 1];

        if (!prop) {
          return <Skeleton width={50} />;
        }

        const type = columnsConfig[prop].type;
        const value = row[prop];

        switch (type) {
          case DimensionColumnDataType.URL:
            try {
              const url = new URL(value);

              return (
                <a href={url.toString()} target="_blank" rel="noreferrer">
                  {value}
                </a>
              );
            } catch {
              return value;
            }
          case DimensionColumnDataType.DATE:
            return formatDate(value);
          case DimensionColumnDataType.MEDIA: {
            if (!value) {
              return value;
            }

            const {
              type,
              value: mediaName,
              url,
            } = parseMediaColumnValue(value);

            if (type === 'image') {
              return <ImageMediaCell label={mediaName} url={url} />;
            }

            return mediaName;
          }
          default:
            return value;
        }
      },
      width:
        columnsWidth[GRID_TREE_DATA_GROUPING_FIELD] ||
        DEFAULT_GROUPING_COLUMN_WIDTH,
    },
  };
};
